import axios from "axios";

// Resolve API base URL from env or global injection so local/prod don’t need code changes
const RESOLVED_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  (typeof window !== "undefined" && window.__API_BASE_URL__) ||
  "http://localhost:5123";

// API Configuration
const API_CONFIG = {
  BASE_URL: RESOLVED_BASE_URL,
  ENDPOINTS: {
    // REST Endpoints
    INCIDENTS: "/api/Incidents",
    INCIDENTS_UPLOAD: (incidentName) => `/api/Incidents/${incidentName}/upload`,
    INCIDENT_ESCALATE: (incidentId) => `/api/Incidents/${incidentId}/escalate`,
    INCIDENT_RESPONSE_ACTIONS: (incidentId) =>
      `/api/incidents/${incidentId}/response-actions`,
    INCIDENT_RESPONSE_ACTION: (incidentId, responseActionId) =>
      `/api/incidents/${incidentId}/response-actions/${responseActionId}`,
    INCIDENT_RESPONSE_ACTIONS_REORDER: (incidentId) =>
      `/api/incidents/${incidentId}/response-actions/reorder`,
    INCIDENT_RESPONSE_ACTIONS_BUNDLE: (incidentId) =>
      `/api/incidents/${incidentId}/actions-bundle`,
    INCIDENT_RECOMMENDATIONS: (incidentId) =>
      `/api/incidents/${incidentId}/recommendations`,
    INCIDENT_RECOMMENDATION: (incidentId, recommendationId) =>
      `/api/incidents/${incidentId}/recommendations/${recommendationId}`,
    RECOMMENDATION_TEMPLATES: "/api/RecommendationTemplates",
    RECOMMENDATION_TEMPLATE: (templateId) =>
      `/api/RecommendationTemplates/${templateId}`,
    USERS: "/api/Users",
    USERS_CREATE: "/api/Users/create",
    USERS_UPDATE: (id) => `/api/Users/update/${id}`,
    USERS_DELETE: (id) => `/api/Users/delete/${id}`,
    CUSTOMER_REQUESTS: "/api/CustomerRequest",
    CUSTOMER_REQUEST_WITH_ATTACHMENTS: "/api/CustomerRequest/with-attachments",
    CUSTOMER_REQUEST: (id) => `/api/CustomerRequest/${id}`,
    CUSTOMER_REQUEST_UPLOAD: (id) => `/api/CustomerRequest/${id}/upload`,
    CUSTOMER_REQUEST_ATTACHMENTS: (id) =>
      `/api/CustomerRequest/${id}/attachments`,
    CUSTOMER_REQUEST_ATTACHMENT_DOWNLOAD: (attachmentId) =>
      `/api/CustomerRequest/attachments/${attachmentId}/download`,
    CUSTOMER_REQUEST_ATTACHMENT_DELETE: (attachmentId) =>
      `/api/CustomerRequest/attachments/${attachmentId}`,
    CUSTOMER_REQUEST_CHAT_MESSAGES: (id) =>
      `/api/CustomerRequestChat/${id}/messages`,
    CUSTOMER_REQUEST_CHAT_HUB: "/hubs/customer-request-chat",
    ONBOARDING_CLIENT: "/api/onboarding/client",
    ONBOARDING_WORKSPACE: "/api/onboarding/workspace",
    ONBOARDING_ASSET: "/api/onboarding/asset",
    ONBOARDING_CLIENTS: "/api/onboarding/clients",
    ONBOARDING_CLIENT_STATUS: (clientUUID) =>
      `/api/onboarding/client/${clientUUID}/status`,
    ONBOARDING_TREE: "/api/Onboarding/tree",
    RESPONSE_ACTION_TEMPLATES: "/api/ResponseActionTemplates",
    RESPONSE_ACTION_TEMPLATE: (templateId) =>
      `/api/ResponseActionTemplates/${templateId}`,
    // SignalR Hub Endpoints
    CHAT_HUB: "/hubs/incident-chat",

    // Chat Message History
    CHAT_MESSAGES: (incidentId) => `/api/Chat/incidents/${incidentId}/messages`,

    // Alert Rules Endpoints
    ALERT_RULES: "/api/AlertRules",
    ALERT_RULE: (ruleId) => `/api/AlertRules/${ruleId}`,
    ALERT_RULE_TOGGLE: (ruleId) => `/api/AlertRules/${ruleId}/toggle`,
    ALERT_RULES_REORDER: "/api/AlertRules/reorder",
    ALERT_RULES_CONDITIONS: "/api/AlertRules/conditions",
    ALERT_RULES_WORKSPACES: "/api/AlertRules/workspaces",
    LOGIC_APP_TRIGGER: "/api/LogicApp/trigger",
    WORKSPACE_LOG_METRICS: "/api/WorkspaceLogMetrics",
    RISKY_ENDPOINTS: "/api/WorkspaceLogMetrics/risky-endpoints",

    // Release Notifications Endpoints (Admin - Helxon Only)
    RELEASE_NOTIFICATIONS: "/api/ReleaseNotifications",
    RELEASE_NOTIFICATION: (notificationId) => `/api/ReleaseNotifications/${notificationId}`,
    RELEASE_NOTIFICATION_PUBLISH: (notificationId) => `/api/ReleaseNotifications/${notificationId}/publish`,

    // User Notifications Endpoints (for header bell icon)
    USER_NOTIFICATIONS: "/api/UserNotifications",
    USER_NOTIFICATION_READ: (notificationId) => `/api/UserNotifications/${notificationId}/read`,
    USER_NOTIFICATIONS_READ_ALL: "/api/UserNotifications/read-all",
    USER_NOTIFICATIONS_UNREAD_COUNT: "/api/UserNotifications/unread-count",

    // Incident AI Analysis
    INCIDENT_ANALYZE: (incidentName) => `/api/incidents/${incidentName}/analyze`,

    // Incident details
    INCIDENT_DETAILS: (incidentName) => `/api/Incidents/${incidentName}/details`,
    INCIDENT_UPDATE: (incidentName) => `/api/Incidents/${incidentName}/update`,
    INCIDENT_ASSIGNABLE_USERS: "/api/Incidents/assignable-users",

    // Incident Attachments
    INCIDENT_ATTACHMENT_DOWNLOAD: (attachmentId) => `/api/Incidents/attachments/${attachmentId}/download`,
    INCIDENT_ATTACHMENT_DELETE: (attachmentId) => `/api/Incidents/attachments/${attachmentId}`,

    // Tag/Mention Notifications Endpoints (for header message icon)
    TAG_NOTIFICATIONS: "/api/TagNotifications",
    TAG_NOTIFICATION_READ: (notificationId) => `/api/TagNotifications/${notificationId}/read`,
    TAG_NOTIFICATIONS_READ_ALL: "/api/TagNotifications/read-all",
    TAG_NOTIFICATIONS_UNREAD_COUNT: "/api/TagNotifications/unread-count",

    // Send Tag Notification (when user mentions @client in chat)
    SEND_TAG_NOTIFICATION: "/api/TagNotifications/send",

    // Get clients available for @mention tagging
    // Helxon users: Returns ALL active clients
    // Other client users: Returns their OWN client + Helxon client
    TAG_NOTIFICATION_CLIENTS: "/api/TagNotifications/clients",

    // Security Reports (Azure Sentinel-backed)
    REPORTS:                     "/api/Reports",
    REPORTS_BY_CLIENT:           (clientUuid) => `/api/Reports?clientUuid=${clientUuid}`,
    REPORT:                      (reportId)   => `/api/Reports/${reportId}`,
    REPORTS_GENERATE:            "/api/Reports/generate",
    REPORT_DOWNLOAD:             (reportId)   => `/api/Reports/${reportId}/download`,
    REPORT_DELETE:               (reportId)   => `/api/Reports/${reportId}`,
    REPORTS_CLIENTS:             "/api/Reports/clients",

    // RBAC - Role-Based Access Control Endpoints
    AUTH_PERMISSIONS: "/api/auth/permissions",
    AUTH_ROLES: "/api/auth/roles", // Get available roles for dropdowns
    AUTH_PERMISSIONS_MATRIX: "/api/auth/roles/permissions-matrix", // Get screens + roles with permissions (admin)
    AUTH_ROLE_PERMISSIONS: (roleId) => `/api/auth/roles/${roleId}/permissions`, // Update role permissions
  },
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to attach Clerk's bearer token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get Clerk instance from window (available when ClerkProvider is mounted)
      // In Clerk React v5, Clerk exposes itself on window.Clerk
      const clerk = window.Clerk;

      if (clerk) {
        // Check if user is authenticated and has a session
        if (clerk.session) {
          // Get the session token from Clerk
          const token = await clerk.session.getToken();

          console.log("token", token);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      }
    } catch (error) {
      // If there's an error getting the token, log it but don't fail the request
      // This allows the request to proceed even if token retrieval fails
      console.warn("Failed to get Clerk token:", error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling (optional)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle common errors (e.g., 401 unauthorized)
    if (error.response?.status === 401) {
      console.warn("Unauthorized request - token may be invalid or expired");
    }
    return Promise.reject(error);
  }
);

export default API_CONFIG;
export { apiClient };
